'use client';

import React, { forwardRef } from 'react';
import { TrendingUp, TrendingDown, Minus, Binoculars } from 'lucide-react';
import { TwitterProfileAnalysis } from '@/types/twitter-analysis';

interface ReconIntelShareCardProps {
  analysis: TwitterProfileAnalysis;
  entityName?: string;
}

/**
 * A beautiful shareable card for RECON Intel
 * Designed to look stunning when shared on X
 * Uses inline styles to ensure they're captured by html-to-image
 */
const ReconIntelShareCard = forwardRef<HTMLDivElement, ReconIntelShareCardProps>(
  ({ analysis, entityName }, ref) => {
    // Sentiment represents what this NEWS means for the Abstract ecosystem
    // (NOT whether the account "likes" Abstract - but whether the news is good/bad)
    const getSentimentConfig = (sentiment: string) => {
      switch (sentiment) {
        case 'bullish':
          return {
            gradient: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
            label: 'Bullish',
            icon: TrendingUp,
            accentColor: '#10b981',
            bgGlow: 'radial-gradient(ellipse at top right, rgba(16, 185, 129, 0.15) 0%, transparent 50%)',
          };
        case 'bearish':
          return {
            gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 50%, #f87171 100%)',
            label: 'Bearish',
            icon: TrendingDown,
            accentColor: '#ef4444',
            bgGlow: 'radial-gradient(ellipse at top right, rgba(239, 68, 68, 0.15) 0%, transparent 50%)',
          };
        case 'informational':
          return {
            gradient: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)',
            label: 'Info',
            icon: Binoculars,
            accentColor: '#3b82f6',
            bgGlow: 'radial-gradient(ellipse at top right, rgba(59, 130, 246, 0.15) 0%, transparent 50%)',
          };
        default:
          return {
            gradient: 'linear-gradient(135deg, #525252 0%, #737373 50%, #a3a3a3 100%)',
            label: 'Neutral',
            icon: Minus,
            accentColor: '#737373',
            bgGlow: 'radial-gradient(ellipse at top right, rgba(115, 115, 115, 0.15) 0%, transparent 50%)',
          };
      }
    };

    const config = getSentimentConfig(analysis.sentiment);
    const SentimentIcon = config.icon;

    // Format text with highlights
    const formatSummary = (text: string) => {
      // Simple version that just returns text for the share card
      // More complex formatting can cause issues with html-to-image
      return text;
    };

    return (
      <div
        ref={ref}
        style={{
          width: '100%',
          maxWidth: '540px',
          minWidth: '300px',
          padding: '20px',
          background: `linear-gradient(180deg, #0a0a0a 0%, #171717 100%)`,
          borderRadius: '16px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          position: 'relative',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Background glow effect */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: config.bgGlow,
            pointerEvents: 'none',
          }}
        />

        {/* Decorative corner accent */}
        <div
          style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            background: config.gradient,
            opacity: 0.1,
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />

        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          {/* Left side - Logo + Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              aria-label="Descout"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #E8A832 0%, #059669 100%)',
                border: `2px solid ${config.accentColor}`,
                boxShadow: `0 0 16px ${config.accentColor}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Binoculars style={{ width: '20px', height: '20px', color: '#ffffff' }} />
            </div>
            <div>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#ffffff',
                  letterSpacing: '-0.02em',
                }}
              >
                RECON Intel
              </div>
              {entityName && (
                <div
                  style={{
                    fontSize: '13px',
                    color: '#a3a3a3',
                    marginTop: '1px',
                  }}
                >
                  @{entityName}
                </div>
              )}
            </div>
          </div>

          {/* Sentiment Badge */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '6px 14px',
              background: config.gradient,
              borderRadius: '16px',
              boxShadow: `0 4px 12px ${config.accentColor}40`,
            }}
          >
            <SentimentIcon
              style={{
                width: '14px',
                height: '14px',
                color: '#ffffff',
              }}
            />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#ffffff',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {config.label}
            </span>
          </div>
        </div>

        {/* Summary Text */}
        <div
          style={{
            fontSize: '15px',
            lineHeight: '1.55',
            color: '#e5e5e5',
            marginBottom: '14px',
            position: 'relative',
          }}
        >
          {formatSummary(analysis.summary)}
        </div>

        {/* Key Topics */}
        {analysis.keyTopics && analysis.keyTopics.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '16px',
            }}
          >
            {analysis.keyTopics.slice(0, 4).map((topic, i) => (
              <span
                key={i}
                style={{
                  fontSize: '12px',
                  padding: '5px 10px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '14px',
                  color: '#d4d4d4',
                }}
              >
                {topic}
              </span>
            ))}
          </div>
        )}

        {/* Upcoming Events */}
        {analysis.upcomingEvents && analysis.upcomingEvents.length > 0 && (
          <div
            style={{
              marginBottom: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            {analysis.upcomingEvents.slice(0, 2).map((event, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '10px',
                }}
              >
                <span style={{ fontSize: '13px', color: '#93c5fd' }}>
                  {event.event}
                </span>
                <span style={{ fontSize: '11px', color: '#60a5fa', fontWeight: '500' }}>
                  {event.timeframe}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Actionable Insight */}
        {analysis.actionableInsight && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 12px',
              borderLeft: '3px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '0 8px 8px 0',
            }}
          >
            <p
              style={{
                fontSize: '13px',
                color: '#a3a3a3',
                fontStyle: 'italic',
                lineHeight: '1.5',
                margin: 0,
              }}
            >
              {analysis.actionableInsight}
            </p>
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: '12px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <div
            style={{
              fontSize: '12px',
              color: '#737373',
            }}
          >
            Based on {analysis.analyzedTweetCount} tweets
          </div>
          <span
            style={{
              fontSize: '12px',
              fontWeight: '600',
              color: config.accentColor,
              letterSpacing: '0.02em',
            }}
          >
            descout.xyz
          </span>
        </div>
      </div>
    );
  }
);

ReconIntelShareCard.displayName = 'ReconIntelShareCard';

export default ReconIntelShareCard;

